'use client';

import {
  Badge,
  Button,
  Drawer,
  IconButton,
  MarkdownPreview,
  Spinner,
  Text,
  toasterApi,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { SquareLock01Icon, Cancel01Icon } from '@terra/ui-icons';
import { useState } from 'react';
import {
  useSkill,
  useSkillAudit,
  useSkillsMutations,
  useSkillVersions,
} from '../hooks/use-skills';
import type {
  SkillsAuditLabels,
  SkillsDetailLabels,
  SkillsEditorLabels,
  SkillsListLabels,
  SkillsVersionLabels,
} from '../types';
import { SkillEditorDrawer } from './skill-editor-drawer';

type FormatDate = (date: Date) => string;

export function SkillDetailDrawer({
  skillId,
  listLabels,
  editorLabels,
  versionLabels,
  auditLabels,
  detailLabels,
  formatDate,
  applicationId,
  onOpenSkillEditor,
  canEdit = true,
  onClose,
}: {
  skillId: string;
  listLabels: SkillsListLabels;
  editorLabels: SkillsEditorLabels;
  versionLabels: SkillsVersionLabels;
  auditLabels: SkillsAuditLabels;
  detailLabels: SkillsDetailLabels;
  formatDate: FormatDate;
  applicationId: string;
  onOpenSkillEditor?: (request: {
    mode: 'create' | 'edit';
    skillId?: string;
    initialDraft?: {
      slug: string;
      name: string;
      description: string;
      body: string;
    };
  }) => void;
  canEdit?: boolean;
  onClose: () => void;
}) {
  const { skill, isLoading } = useSkill({ skillId, applicationId });
  const { versions } = useSkillVersions({ skillId, applicationId });
  const { entries } = useSkillAudit({ skillId, applicationId });
  const { publishSkill, rollbackSkill, deleteSkill, restoreSkill } =
    useSkillsMutations({ applicationId });
  const [editing, setEditing] = useState(false);
  const isCustomer = skill?.source === 'CUSTOMER';
  const headerVersion = skill?.draftVersion ?? skill?.publishedVersion ?? null;
  const headerName = headerVersion?.frontmatter.name ?? skill?.slug ?? '';
  const headerDescription =
    skill?.publishedVersion?.frontmatter.description ??
    skill?.draftVersion?.frontmatter.description ??
    '';
  const bodyMarkdown =
    skill?.draftVersion?.body ?? skill?.publishedVersion?.body ?? '';
  const handlePublish = async () => {
    try {
      await publishSkill.mutateAsync({ skillId });
      toasterApi.success({
        title: editorLabels.successTitle,
        description: editorLabels.publishedToast,
      });
    } catch {
      toasterApi.error({
        title: editorLabels.errorTitle,
        description: editorLabels.error,
      });
    }
  };

  const handleRollback = async (versionId: string) => {
    try {
      await rollbackSkill.mutateAsync({ skillId, body: { versionId } });
      toasterApi.success({
        title: editorLabels.successTitle,
        description: editorLabels.rolledBackToast,
      });
    } catch {
      toasterApi.error({
        title: editorLabels.errorTitle,
        description: editorLabels.error,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSkill.mutateAsync(skillId);
      toasterApi.success({
        title: editorLabels.successTitle,
        description: editorLabels.deletedToast,
      });
      onClose();
    } catch {
      toasterApi.error({
        title: editorLabels.errorTitle,
        description: editorLabels.error,
      });
    }
  };

  const handleRestore = async () => {
    try {
      await restoreSkill.mutateAsync(skillId);
      toasterApi.success({
        title: editorLabels.successTitle,
        description: editorLabels.restoredToast,
      });
    } catch {
      toasterApi.error({
        title: editorLabels.errorTitle,
        description: editorLabels.error,
      });
    }
  };

  return (
    <>
      <Drawer.Root open onOpenChange={({ open }) => !open && onClose()}>
        <Drawer.Backdrop />
        <Drawer.Positioner w="90vw" maxW="1500px">
          <Drawer.Content h="full">
            <Drawer.Header>
              <VStack alignItems="flex-start" gap={1} pr={8} w="full">
                <HStack gap={2} flexWrap="wrap">
                  {skill ? (
                    <>
                      <Badge variant="outline">{skill.agentId}</Badge>
                      <Badge variant={isCustomer ? 'outline' : 'fill'}>
                        {isCustomer ? listLabels.customer : listLabels.platform}
                      </Badge>
                      {skill.deletedAt ? (
                        <Badge variant="emphasis" colorScheme="red.9">
                          {listLabels.tombstoned}
                        </Badge>
                      ) : skill.publishedVersionId ? (
                        <Badge variant="outline" colorScheme="green.9">
                          {listLabels.published}
                        </Badge>
                      ) : (
                        <Badge variant="outline">{listLabels.draft}</Badge>
                      )}
                      {skill.draftVersion ? (
                        <Badge variant="outline">
                          {listLabels.draftBadgeLabel}
                        </Badge>
                      ) : null}
                    </>
                  ) : null}
                </HStack>
                <Drawer.Title fontSize="xl">{headerName}</Drawer.Title>
                {headerDescription ? (
                  <Text fontSize="sm" color="fg.muted" lineHeight="1.6">
                    {headerDescription}
                  </Text>
                ) : null}
              </VStack>
              <Drawer.CloseTrigger
                asChild
                position="absolute"
                top="3"
                right="4"
              >
                <IconButton
                  variant="ghost"
                  aria-label={editorLabels.close}
                  size="sm"
                >
                  <Cancel01Icon size={14} />
                </IconButton>
              </Drawer.CloseTrigger>
            </Drawer.Header>
            <Drawer.Body>
              {isLoading || !skill ? (
                <HStack justifyContent="center" py={6}>
                  <Spinner />
                </HStack>
              ) : (
                <VStack gap={6} alignItems="stretch">
                  <Section title={detailLabels.identityTitle}>
                    <Box
                      borderRadius="md"
                      borderWidth="1px"
                      borderStyle="solid"
                      borderColor="border.neutral.muted"
                      bg="bg.default"
                      p={3}
                    >
                      <VStack alignItems="stretch" gap={2}>
                        <DetailRow
                          label={detailLabels.slugLabel}
                          value={skill.slug}
                          mono
                        />
                        <DetailRow
                          label={detailLabels.statusLabel}
                          value={
                            skill.deletedAt
                              ? listLabels.tombstoned
                              : skill.publishedVersionId
                                ? listLabels.published
                                : listLabels.draft
                          }
                        />
                      </VStack>
                    </Box>
                    {isCustomer ? (
                      <HStack
                        gap={2}
                        alignItems="flex-start"
                        borderRadius="md"
                        borderWidth="1px"
                        borderStyle="solid"
                        borderColor="border.neutral.muted"
                        bg="bg.neutral.subtleSoft"
                        p={3}
                      >
                        <Box color="fg.muted" mt={0.5}>
                          <SquareLock01Icon size={12} />
                        </Box>
                        <Text fontSize="xs" color="fg.muted" lineHeight="1.6">
                          {detailLabels.tenantScopeNote}
                        </Text>
                      </HStack>
                    ) : null}
                  </Section>

                  <Section title={detailLabels.triggerTitle}>
                    {headerDescription ? (
                      <Box
                        borderRadius="md"
                        borderLeftWidth="3px"
                        borderLeftColor="border.neutral.default"
                        bg="bg.default"
                        px={3}
                        py={2}
                      >
                        <Text fontSize="sm" color="fg.default" lineHeight="1.6">
                          {headerDescription}
                        </Text>
                      </Box>
                    ) : (
                      <Text fontSize="sm" color="fg.muted">
                        {detailLabels.triggerEmpty}
                      </Text>
                    )}
                  </Section>

                  <Section title={detailLabels.instructionsTitle}>
                    <Box
                      borderRadius="md"
                      borderWidth="1px"
                      borderStyle="solid"
                      borderColor="border.neutral.muted"
                      bg="bg.default"
                      px={4}
                      py={3}
                      maxH="420px"
                      overflowY="auto"
                    >
                      {bodyMarkdown.trim().length === 0 ? (
                        <Text fontSize="sm" color="fg.muted">
                          {detailLabels.instructionsEmpty}
                        </Text>
                      ) : (
                        <MarkdownPreview source={bodyMarkdown} />
                      )}
                    </Box>
                  </Section>

                  <Section title={versionLabels.title}>
                    {versions.length === 0 ? (
                      <Text fontSize="sm" color="fg.muted">
                        {versionLabels.noHistory}
                      </Text>
                    ) : (
                      <VStack gap={2} alignItems="stretch">
                        {versions.map((version) => {
                          const isCurrent =
                            version.versionId === skill.publishedVersionId;

                          return (
                            <HStack
                              key={version.versionId}
                              justifyContent="space-between"
                              p={2}
                              borderWidth="1px"
                              borderColor="border.subtle"
                              borderRadius="md"
                            >
                              <VStack alignItems="start" gap={0.5}>
                                <HStack gap={1.5} alignItems="center">
                                  <Text fontSize="sm" fontWeight="medium">
                                    {versionLabels.versionLabel(
                                      version.version,
                                    )}
                                  </Text>
                                  {isCurrent ? (
                                    <Badge variant="outline">
                                      {versionLabels.current}
                                    </Badge>
                                  ) : null}
                                </HStack>
                                <Text fontSize="xs" color="fg.muted">
                                  {version.publishedAt
                                    ? versionLabels.publishedAtLabel(
                                        formatDate(
                                          new Date(version.publishedAt),
                                        ),
                                      )
                                    : versionLabels.draft}
                                </Text>
                              </VStack>
                              {isCustomer &&
                              !isCurrent &&
                              version.publishedAt ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleRollback(version.versionId)
                                  }
                                  loading={rollbackSkill.isPending}
                                >
                                  {versionLabels.rollbackTo}
                                </Button>
                              ) : null}
                            </HStack>
                          );
                        })}
                      </VStack>
                    )}
                  </Section>

                  <Section title={auditLabels.title}>
                    {entries.length === 0 ? (
                      <Text fontSize="sm" color="fg.muted">
                        {auditLabels.noActivity}
                      </Text>
                    ) : (
                      <VStack gap={1} alignItems="stretch">
                        {entries.map((entry) => (
                          <HStack
                            key={entry.auditId}
                            justifyContent="space-between"
                            fontSize="sm"
                          >
                            <Text>
                              {auditLabels.actionLabel(entry.action)}{' '}
                              {entry.actorType === 'PLATFORM' ||
                              entry.actorType === 'system'
                                ? auditLabels.byPlatform
                                : entry.actorId
                                  ? auditLabels.byUserLabel(entry.actorId)
                                  : ''}
                            </Text>
                            <Text color="fg.muted">
                              {formatDate(new Date(entry.createdAt))}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </Section>
                </VStack>
              )}
            </Drawer.Body>
            {isCustomer && skill && canEdit ? (
              <Drawer.Footer
                gap={2}
                p={4}
                borderTopWidth="1px"
                borderTopColor="border.default"
                bg="bg.subtle"
              >
                <HStack gap={2} w="full" justifyContent="flex-end">
                  {skill.deletedAt ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRestore}
                      loading={restoreSkill.isPending}
                    >
                      {editorLabels.restore}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        colorScheme="red.9"
                        onClick={handleDelete}
                        loading={deleteSkill.isPending}
                      >
                        {editorLabels.delete}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          onOpenSkillEditor
                            ? onOpenSkillEditor({
                                mode: 'edit',
                                skillId,
                                initialDraft: {
                                  slug: skill.slug,
                                  name: headerName,
                                  description: headerDescription,
                                  body: bodyMarkdown,
                                },
                              })
                            : setEditing(true)
                        }
                      >
                        {editorLabels.edit}
                      </Button>
                      {skill.draftVersion ? (
                        <Button
                          size="sm"
                          onClick={handlePublish}
                          loading={publishSkill.isPending}
                        >
                          {editorLabels.publish}
                        </Button>
                      ) : null}
                    </>
                  )}
                </HStack>
              </Drawer.Footer>
            ) : null}
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
      {editing && skill && canEdit && !onOpenSkillEditor ? (
        <SkillEditorDrawer
          mode="edit-draft"
          skill={skill}
          labels={editorLabels}
          applicationId={applicationId}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <VStack alignItems="stretch" gap={2}>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="fg.default"
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {title}
      </Text>
      {children}
    </VStack>
  );
}

function DetailRow({
  label,
  value,
  mono,
  help,
  rightAdornment,
}: {
  label: string;
  value: string;
  mono?: boolean;
  help?: string;
  rightAdornment?: React.ReactNode;
}) {
  return (
    <VStack alignItems="stretch" gap={0.5}>
      <HStack justifyContent="space-between" alignItems="center" gap={3}>
        <Text
          fontSize="2xs"
          color="fg.muted"
          textTransform="uppercase"
          letterSpacing="wide"
        >
          {label}
        </Text>
        {rightAdornment}
      </HStack>
      <Text
        fontSize="sm"
        color="fg.default"
        fontFamily={mono ? 'mono' : undefined}
        wordBreak="break-all"
      >
        {value}
      </Text>
      {help ? (
        <Text fontSize="2xs" color="fg.muted" lineHeight="1.6">
          {help}
        </Text>
      ) : null}
    </VStack>
  );
}
